/**
 * Transport over the OnlineClient's relay `BinaryChannel` (binary frames on the lobby WebSocket; the server
 * forwards them verbatim to the other peer). The channel is reliable and ordered, which the session tolerates
 * (it is written for unreliable/unordered delivery). Closing the transport only unsubscribes: the WebSocket
 * belongs to the OnlineClient.
 */
import type { BinaryChannel, Transport, TransportState } from '@hf/shared';
import { TransportCore } from './transport-core';
import type { CreateRelayTransport } from './types';

export const createRelayTransport: CreateRelayTransport = (channel: BinaryChannel) => {
  const core = new TransportCore('relay', channel.open ? 'open' : 'connecting');
  const offBinary = channel.onBinary((data) => {
    // The first frame proves the channel is up even if `open` was read early.
    if (core.state === 'connecting') core.setState('open');
    core.emit(data);
  });
  const offClose = channel.onClose(() => {
    unsubscribe();
    core.setState('closed');
  });
  let subscribed = true;
  const unsubscribe = (): void => {
    if (!subscribed) return;
    subscribed = false;
    offBinary();
    offClose();
  };

  const transport: Transport = {
    kind: 'relay',
    get state(): TransportState {
      if (core.state === 'connecting' && channel.open) core.setState('open');
      return core.state;
    },
    stats: core.stats,
    send(data) {
      if (core.state === 'closed' || !channel.open) return;
      if (core.state === 'connecting') core.setState('open');
      try {
        // Copy: the session reuses its packet buffers and the channel owner may queue the frame.
        channel.send(data.slice());
        core.countSent(data.byteLength);
      } catch {
        // socket closing: drop (the session's redundancy and timeouts cover it)
      }
    },
    onMessage: (h) => core.onMessage(h),
    onStateChange: (h) => core.onStateChange(h),
    reportRtt: (ms) => core.reportRtt(ms),
    close() {
      unsubscribe();
      core.setState('closed');
    },
  };
  return transport;
};
